import type { FamilyClaimAcquisition } from "./acquisition-steps";

export type LastSentEmail = FamilyClaimAcquisition["lastSentEmail"];

export type Tone = "success" | "warning" | "error" | "secondary";

/**
 * Postmark's delivery lifecycle as a jurist reads it. A Map rather than an object: these keys come
 * off the wire, and `status` is a free VARCHAR — Front writes it too.
 */
const STATUS_BADGES = new Map<string, { label: string; tone: Tone }>([
  ["PENDING", { label: "En cours d'envoi", tone: "secondary" }],
  ["DELIVERED", { label: "Délivré", tone: "success" }],
  ["OPENED", { label: "Ouvert", tone: "success" }],
  ["CLICKED", { label: "Cliqué", tone: "success" }],
  ["BOUNCED", { label: "Rejeté", tone: "error" }],
  ["SPAM_COMPLAINT", { label: "Signalé comme spam", tone: "error" }],
]);

/**
 * An unrecognised status shows through as-is rather than being hidden: it means a sender wrote one
 * this map has not met, and a jurist staring at a raw value will tell us faster than silence will.
 */
export function getEmailStatus(status: string): { label: string; tone: Tone } {
  return STATUS_BADGES.get(status) ?? { label: status, tone: "secondary" };
}

/**
 * Postmark's bounce groups in French — the short form of the sentences the bounce tasks are titled
 * with (`report-email-bounce.ts`, API side).
 *
 * Red is for an address that will not work again without a human fixing it; amber for the ones that
 * may clear on their own, and for an unsubscribe, which is a decision rather than a failure.
 */
const BOUNCE_CAUSES = new Map<string, { label: string; tone: Tone }>([
  ["HARD_BOUNCE", { label: "Adresse inexistante", tone: "error" }],
  ["BLOCKED", { label: "Envoi bloqué", tone: "error" }],
  ["INACTIVE_RECIPIENT", { label: "Adresse désactivée", tone: "error" }],
  ["SPAM_COMPLAINT", { label: "Signalé comme spam", tone: "error" }],
  ["SOFT_BOUNCE", { label: "Boîte pleine", tone: "warning" }],
  ["TRANSIENT", { label: "Serveur injoignable", tone: "warning" }],
  ["CHALLENGE_VERIFICATION", { label: "Approbation demandée", tone: "warning" }],
  ["UNSUBSCRIBE", { label: "Désinscription demandée", tone: "warning" }],
]);

/** Same rule as the statuses: an unmapped cause shows through rather than being hidden. */
export function getBounceCause(cause: string): { label: string; tone: Tone } {
  return BOUNCE_CAUSES.get(cause) ?? { label: cause, tone: "error" };
}
